import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { PopinPriceDetailComponent } from '@components/popin-price-detail';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageComponent } from '@shared/components/image';
import { MAX_PIXEL_DENSITY } from '@shared/components/image/models';
import { getCDNLink } from '@shared/components/image/utils';
import { SvgComponent } from '@shared/components/svg';
import { Kitchen, KitchenVariant } from '@shared/models';
import { DialogService } from '@shared/services/dialog';
import { ImageService } from '@shared/services/image';
import { PreloadVariant } from '@shared/services/image/models';
import { PlatformContextService } from '@shared/services/platform-context';
import { UrlParamsService } from '@shared/services/url-params';
import { moveItemToFront, observeResize } from '@shared/utils';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

@Component({
  selector: 'app-kitchen-model-card',
  imports: [ImageComponent, SvgComponent, RouterLink, TranslatePipe, NgClass],
  templateUrl: './kitchen-model-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KitchenModelCardComponent implements OnDestroy {
  kitchen = input.required<Kitchen>();
  initialVariantId = input<string>();
  onCardSelected = output();
  readonly kitchenVariants = signal<KitchenVariant[]>(null!);
  readonly preloadVariant = signal<PreloadVariant[]>([]);
  readonly kitchenNameLength = computed(() => this.kitchen().name.length);
  readonly imageSelected = computed(() => this.kitchen().variants[this.variantSelected()]?.image);
  readonly variantIdSelected = computed(() => this.kitchen().variants[this.variantSelected()]?.id);
  readonly variantFrontSelected = computed(() => {
    if (this.variantSelected() === 0) return '';
    const fronts = this.kitchen().variants[this.variantSelected()];
    return fronts?.name ?? '';
  });
  protected readonly visualElement = viewChild.required<ElementRef<HTMLVideoElement>>('visual');
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly dialogService = inject(DialogService);
  private readonly imageService = inject(ImageService);
  private readonly isServer = inject(PlatformContextService).isServer;
  private readonly filterFront = this.urlParamsService.param('filter_front');
  readonly variantSelected = linkedSignal<number>(() => {
    // Get the initial variant id
    const initialVariantId = this.initialVariantId();

    // Get the filter_front parameter value
    const filterLabel = this.filterFront();

    // Get the variants list
    const variants = this.kitchen().variants;

    if (initialVariantId) {
      const initIdx = variants.findIndex((v) => v.id === initialVariantId);
      if (initIdx >= 0) return initIdx;
    }

    // Calculate the maximum number of fronts among all variants
    const maxFronts = variants.reduce((max, v) => {
      const len = v.fronts?.length ?? 0;
      return len > max ? len : max;
    }, 0);

    // First check all fronts at index 0, then all at index 1, etc.
    for (let position = 0; position < maxFronts; position++) {
      // Find first variant where front[position].label === filterLabel
      const idx = variants.findIndex(
        (v) =>
          // Optional chaining to avoid errors if fronts is undefined
          v.fronts?.[position]?.label === filterLabel
      );
      if (idx >= 0) {
        return idx;
      }
    }

    // If no match found, return 0 as default
    return 0;
  });

  constructor() {
    effect(() => {
      const front = untracked(this.filterFront);
      const variantSelected = untracked(this.variantSelected);
      if (front) {
        this.kitchenVariants.set(moveItemToFront(this.kitchen().variants, variantSelected));
      } else {
        this.kitchenVariants.set(this.kitchen().variants);
      }
    });
    effect((onCleanup) => {
      if (this.isServer()) return;

      const visualElement = this.visualElement();
      if (!visualElement) return;

      const el: Element = visualElement.nativeElement;
      const sub = observeResize(el)
        .pipe(
          map(({ contentRect }) => ({ w: contentRect.width, h: contentRect.height })),
          distinctUntilChanged((a, b) => a.w === b.w && a.h === b.h),
          debounceTime(300)
        )
        .subscribe(({ w, h }) => {
          // The size ImageComponent asks the CDN for, or the preloaded file is never the one displayed
          // (spec "preloads the hovered swatch visual at the size the card then displays").
          const density = Math.min(globalThis.devicePixelRatio || 1, MAX_PIXEL_DENSITY);
          const width = Math.round(w * density);
          const height = Math.round(h * density);
          const variants = this.kitchen().variants;

          const preloadVariant = variants.reduce((acc: PreloadVariant[], variant) => {
            const cdnLink = getCDNLink(variant.image, { width, height });
            if (cdnLink) {
              acc.push({
                cdnLink,
                id: variant.id,
              });
            }
            return acc;
          }, []);

          this.preloadVariant.set(preloadVariant);
        });

      onCleanup(() => sub.unsubscribe());
    });
  }

  onVariantSelect(variantId: string): void {
    const variantIndex = this.kitchen().variants.findIndex((v) => v.id === variantId);
    this.variantSelected.set(variantIndex);
  }

  openPriceDetailsDialog(): void {
    this.dialogService.openDialog(PopinPriceDetailComponent, 'BIG', {
      data: {
        id: this.kitchen().id,
        shortRef: this.kitchen().implantation?.shortRef,
      },
    });
  }

  ngOnDestroy(): void {
    this.imageService.clearAllImagePreloads();
  }

  protected onImageHover(id: string) {
    if (this.imageService.preloadIdLoaded.has(id)) return;
    const variant = this.preloadVariant().find((v) => v.id === id);
    if (variant) this.imageService.setImagePreload(variant.cdnLink, id);
  }
}
